import type { ActivityContext, ActivityHandle } from '../../core/activity';
import { makeDraggable } from '../../shared/drag-drop';
import levelsData from './levels.json';
import '../ordering_numbers/style.css';
import './style.css';

interface Level {
  level: number;
  objective: string;
  difficulty: number;
  frases: string[];
}

const LEVELS = (levelsData as { levels: Level[] }).levels;

interface PoolItem {
  value: string;
  ordem: number;
  placed: boolean;
  wrongFlash: boolean;
}

export function mountOrderingSentences(ctx: ActivityContext): ActivityHandle {
  const level = LEVELS.find((l) => l.level === ctx.level) ?? LEVELS[0];
  if (!level) {
    ctx.root.innerHTML = '<p>Nível não encontrado.</p>';
    return { unmount: () => { ctx.root.innerHTML = ''; } };
  }

  const frase = level.frases[Math.floor(Math.random() * level.frases.length)]!;
  const palavras = frase.split(' ');
  // Palavra repetida na frase: a ordem desempata (cada ficha sabe sua posição,
  // mas qualquer ficha com o MESMO texto vale pra posição esperada).
  const pool: PoolItem[] = shuffle(palavras.map((p, i) => ({ value: p, ordem: i, placed: false, wrongFlash: false })));
  const ordered: string[] = [];

  const page = document.createElement('div');
  page.className = 'activity-page';
  page.innerHTML = `
    <header class="activity-bar">
      <button class="activity-bar__back" type="button" aria-label="Voltar ao hub">← Voltar</button>
      <div class="activity-bar__title">Montar a frase</div>
      <div class="activity-bar__level">Nível ${level.level} de ${LEVELS.length}</div>
    </header>
    <section class="activity-stage ordering">
      <div class="feedback" aria-live="polite"></div>
      <p class="ordering__mode">Monte a frase na ordem certa</p>
      <p class="ordering__hint" aria-live="polite"></p>
      <div class="ordering__sequence frase__montada" aria-label="Frase montada" aria-live="polite"></div>
      <div class="ordering__pool frase__palavras" role="list" aria-label="Palavras embaralhadas"></div>
    </section>
  `;
  ctx.root.appendChild(page);

  const sequenceEl = page.querySelector<HTMLElement>('.ordering__sequence')!;
  const poolEl = page.querySelector<HTMLElement>('.ordering__pool')!;
  const hintEl = page.querySelector<HTMLElement>('.ordering__hint')!;
  const feedback = page.querySelector<HTMLElement>('.feedback')!;
  page.querySelector<HTMLElement>('.activity-bar__back')!.addEventListener('click', () => ctx.onExit());

  function renderAll(): void {
    renderSequence();
    renderPool();
    renderHint();
  }

  function renderSequence(): void {
    sequenceEl.innerHTML = '';
    for (let i = 0; i < palavras.length; i++) {
      const slot = document.createElement('div');
      slot.className = 'ordering__slot frase__slot';
      if (i < ordered.length) {
        slot.classList.add('ordering__slot--filled');
        slot.textContent = ordered[i]!;
      } else {
        slot.textContent = '···';
        if (i === ordered.length) {
          slot.classList.add('ordering__slot--next');
          slot.dataset['dropNext'] = '1';
        }
      }
      sequenceEl.appendChild(slot);
    }
  }

  function renderPool(): void {
    poolEl.innerHTML = '';
    pool.forEach((item, i) => {
      if (item.placed) return;
      const el = document.createElement('button');
      el.type = 'button';
      el.className = 'ordering__num frase__palavra';
      el.dataset.idx = String(i);
      el.textContent = item.value;
      if (item.wrongFlash) el.classList.add('ordering__num--wrong');
      el.addEventListener('click', () => clickPalavra(i));
      poolEl.appendChild(el);
      makeDraggable(el, {
        getDropTarget: (x, y) => {
          const e = document.elementFromPoint(x, y);
          return e?.closest<HTMLElement>('[data-drop-next]') ?? null;
        },
        onDrop: () => clickPalavra(i),
      });
    });
  }

  function renderHint(): void {
    if (ordered.length === palavras.length) {
      hintEl.textContent = '';
      return;
    }
    hintEl.textContent = ordered.length === 0
      ? 'Qual palavra começa a frase? Clique nela.'
      : 'Qual vem agora? Leia o que já montou.';
  }

  function showFeedback(kind: 'ok' | 'err'): void {
    feedback.textContent = kind === 'ok' ? '✓' : '✗';
    feedback.className = `feedback feedback--show feedback--${kind}`;
    window.setTimeout(() => feedback.classList.remove('feedback--show'), 500);
  }

  function clickPalavra(idx: number): void {
    const item = pool[idx]!;
    if (item.placed) return;
    const esperada = palavras[ordered.length];
    if (esperada === undefined) return;
    if (item.value === esperada) {
      item.placed = true;
      ordered.push(item.value);
      ctx.audio.play('correct');
      renderAll();
      if (ordered.length === palavras.length) {
        window.setTimeout(finishLevel, 600);
      }
    } else {
      item.wrongFlash = true;
      ctx.audio.play('wrong');
      showFeedback('err');
      renderPool();
      window.setTimeout(() => {
        item.wrongFlash = false;
        renderPool();
      }, 500);
    }
  }

  function finishLevel(): void {
    ctx.audio.play('win');
    ctx.onComplete(level.level, palavras.length);
    const stage = page.querySelector<HTMLElement>('.activity-stage')!;
    stage.innerHTML = `
      <h2 class="activity-finish__title">Nível ${level.level} concluído!</h2>
      <p class="activity-finish__text">Você montou: “${frase}”</p>
      <div class="activity-finish__actions">
        <button class="btn btn--secondary" type="button" data-act="hub">Voltar ao hub</button>
        ${level.level < LEVELS.length
          ? `<button class="btn btn--primary" type="button" data-act="next">Próximo nível</button>`
          : ''}
      </div>
    `;
    stage.querySelector<HTMLElement>('[data-act="hub"]')!.addEventListener('click', () => ctx.onExit());
    const next = stage.querySelector<HTMLElement>('[data-act="next"]');
    if (next) {
      next.addEventListener('click', () => {
        window.location.hash = `/ordering_sentences/${level.level + 1}`;
      });
    }
  }

  renderAll();

  return {
    unmount(): void {
      ctx.root.innerHTML = '';
    },
  };
}

function shuffle<T>(arr: T[]): T[] {
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [arr[i], arr[j]] = [arr[j]!, arr[i]!];
  }
  return arr;
}
